import React, { useState, useEffect } from 'react';

import { Row, Col, Input, Button, FormGroup, Label } from 'reactstrap';

import Required from 'components/Required';
import AttachModal from 'components/AttachModal';
import config from '../../config';
import { bytesToSize, getRequiredAttachs } from 'helpers';
import { attachs as attachsLabels } from 'services/attachs';
import { RequestFiles, Request } from 'types/Request';

// Mime types aceptados por la aplicación
const accept = ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'];

interface AttachmentsProps {
  files: RequestFiles;
  setFiles: (files: RequestFiles) => void;
  disabled?: boolean;
  editable?: boolean;
  setLoading?: (loading: boolean) => void;
  previouslyAuthorized: boolean;
  formData: Request;
}

export default function Attachments(props: AttachmentsProps) {
  const {
    files,
    setFiles,
    disabled = false,
    editable = true,
    setLoading: setExternalLoading,
    previouslyAuthorized = false,
    formData,
  } = props;
  const [attachModal, setAttachModal] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [required, setRequired] = useState<(keyof RequestFiles)[]>([]);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [emptyError, setEmptyError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);

  // Parámetros
  const [impairment, setImpairment] = useState(false);

  const handleFileChange = (name: string, fileList: FileList | null) => {
    // Si no se seleccionó ningún archivo, no hacemos nada
    if (!fileList || !fileList.length) {
      return;
    }

    if (!accept.includes(fileList[0].type)) {
      setTypeError(name);
      // Reseteamos el file input
      const fileInput = document.getElementById(
        `file_${name}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      return;
    }

    // Controlamos el tamaño del archivo
    if (fileList[0].size > config.maxFileSize) {
      setSizeError(name);
      // Reseteamos el file input
      const fileInput = document.getElementById(
        `file_${name}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      return;
    }

    if (fileList[0].size === 0) {
      setEmptyError(name);
      // Reseteamos el file input
      const fileInput = document.getElementById(
        `file_${name}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      return;
    }

    setLoading(true);
    if (setExternalLoading) {
      setExternalLoading(true);
    }
    setFiles({ ...files, [name]: fileList[0] });

    setSizeError(null);
    setTypeError(null);

    setLoading(false);
    if (setExternalLoading) {
      setExternalLoading(false);
    }
  };

  const handleFilePreview = (name: keyof RequestFiles) => {
    try {
      setLoading(true);
      if (setExternalLoading) {
        setExternalLoading(true);
      }

      // Si no hay una url definida porque no viene de la BBDD o porque todavía
      // no se previsualizó, creamos una
      // @ts-ignore
      if (!files[name].url) {
        // @ts-ignore
        files[name].url = URL.createObjectURL(files[name]);
      }

      // @ts-ignore
      let safeUrl = files[name]!.url;
      if (typeof safeUrl === 'string' && !safeUrl.startsWith('blob:')) {
        safeUrl = safeUrl
          .replace(/#/g, '%23') // Numeral
          .replace(/\?/g, '%3F') // Interrogación
          .replace(/\+/g, '%2B') // Signo más
          .replace(/&/g, '%26') // Ampersand
          .replace(/=/g, '%3D'); // Igual
      }

      // Los pdfs los abrimos en una nueva pestaña/ventana
      // @ts-ignore
      if (files[name].type === 'application/pdf') {
        // @ts-ignore
        window.open(safeUrl);
        return;
      }

      // Para todo lo demás utilizamos el modal
      setAttachModal({
        show: true,
        url: safeUrl,
        title: attachsLabels[name],
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      if (setExternalLoading) {
        setExternalLoading(false);
      }
    }
  };

  const handleFileReplace = (name: keyof RequestFiles) => {
    const fileInput = document.getElementById(
      `file_${name}`,
    ) as HTMLInputElement;
    fileInput.click();
  };

  const handleFileRemove = (name: keyof RequestFiles) => {
    try {
      setLoading(true);

      // Reseteamos el file input
      const fileInput = document.getElementById(
        `file_${name}`,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }

      // Si tiene una vista previa generada, la borramos
      // @ts-ignore
      if (files[name]?.url && files[name]?.url.substring(0, 4) === 'blob') {
        // @ts-ignore
        URL.revokeObjectURL(files[name]!.url);
      }

      // Quitamos el archivo del state
      setFiles({
        ...files,
        [name]: undefined,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getRequiredValue = (key: keyof RequestFiles) => {
    // COMEI y CASA
    if (
      (formData.insurer_id === 22 || formData.insurer_id === 47) &&
      key === 'historia_clinica'
    ) {
      return false;
    }

    if ([48, 50].includes(formData.insurer_id) && key === 'autorizacion') {
      return false;
    }

    if (formData.insurer_id == 57 && impairment && key === 'frente_orden') {
      return false;
    }

    if (formData.insurer_id == 83 && key === 'autorizacion') {
      return false;
    }

    // Dorso orden opcional excepto COMEI
    if (key === 'dorso_orden' && formData.insurer_id != 22) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    setLoading(disabled);
  }, [disabled]);

  useEffect(() => {
    if (!editable) {
      setRequired(Object.keys(files) as (keyof RequestFiles)[]);
      return;
    }

    let newRequired = required;

    switch (formData.insurer_id) {
      case 86: { // OPDEA
        const bonos: any = [];
        for (let i = 1; i <= formData.sessions.length; i++) {
          bonos.push(`bono_${i}`);
        }

        newRequired = [...getRequiredAttachs(formData.insurer_id), ...bonos];
        break;
      }
      default:
        newRequired = getRequiredAttachs(formData.insurer_id);
    }

    newRequired = newRequired.filter(r => r !== 'certificado_discapacidad');
    if (impairment) {
      if (formData.insurer_id == 74) {
        // Federada
        newRequired.push('planilla_asistencia');
      } else {
        newRequired.push('certificado_discapacidad');
      }

      if (formData.insurer_id === 22 || formData.insurer_id === 57) {
        // COMEI y Swiss Medical
        attachsLabels['certificado_discapacidad'] =
          'Autorización del tratamiento de discapacidad';
      } else if (formData.insurer_id === 26 || formData.insurer_id === 39) {
        // AMFFA y COESBA
        attachsLabels['certificado_discapacidad'] =
          'Certificado de Discapacidad (CUD)';
      } else if (formData.insurer_id === 55) {
        // Medifé
        attachsLabels['certificado_discapacidad'] =
          'Autorización Previa expedida por la OS';
      } else {
        attachsLabels['certificado_discapacidad'] =
          'Certificado de discapacidad';
      }

      if (formData.insurer_id === 57) {
        // Swiss Medical
        newRequired.push('planilla_asistencia');
      }
    }

    if (formData.insurer_id === 44) {
      // OSAM
      newRequired.push('planilla_asistencia');

      if (formData.extended_quota) {
        newRequired.unshift('autorizacion');
        attachsLabels['autorizacion'] = 'Autorización de cupo';
      }
    }

    // Excepto para el caso de discapacidad AMFFA y Swiss todas las otras autorizaciones previa requieren autorización
    // de la obra social. Dada la complejidad de Medife se trata por separado
    if (
      previouslyAuthorized &&
      !(formData.insurer_id === 57 && impairment) &&
      !(formData.insurer_id === 26 && impairment) &&
      formData.insurer_id != 55 &&
      formData.insurer_id != 71 &&
      formData.insurer_id != 83
    ) {
      newRequired.unshift('autorizacion');
    } else if (formData.insurer_id == 55) {
      // Medife
      attachsLabels['autorizacion'] = 'Autorización OOSS';

      if (formData.extended_quota) {
        newRequired.unshift('autorizacion');
        attachsLabels['autorizacion'] = 'Autorización de cupo';
      } else if (
        // RPG sólo con planes bronce y plata
        formData.practice_id == 9 && // RPG
        (formData.recipient_plan.toUpperCase().includes('COBRE') || // Plan cobre
          formData.recipient_plan.toUpperCase().includes('NIQUEL') || // Plan niquel
          formData.recipient_plan.toUpperCase().includes('BRONCE') || // Plan bronce
          formData.recipient_plan.toUpperCase().includes('TITANIO') || // Plan titanio
          formData.recipient_plan.toUpperCase().includes('PLATA')) // Plan plata
      ) {
        newRequired.unshift('autorizacion');
      } else if (formData.practice_id && (formData.impairment)) { // discapacidad
        newRequired.unshift('autorizacion');
      } else if (formData.practice_id == 14) {
        // Internación
        newRequired.unshift('indicacion_medica');
      }
    } else if (formData.insurer_id == 71) { // La Segunda ART
      attachsLabels['frente_orden'] = 'Frente de la Derivación';
      attachsLabels['planilla_asistencia'] = 'Planilla funcional con asistencia';
      attachsLabels['autorizacion'] = 'Autorización emitida por La Segunda ART';

      if (impairment) {
        attachsLabels['certificado_discapacidad'] = 'Certificado de discapacidad';
      }
    } else if (formData.insurer_id == 83) { // Swiss ART
      attachsLabels['frente_orden'] = 'Frente de la Derivación';
      attachsLabels['planilla_asistencia'] = 'Planilla funcional con asistencia';
      attachsLabels['autorizacion'] = 'Autorización emitida por Swiss ART';

      if (impairment) {
        attachsLabels['certificado_discapacidad'] = 'Certificado de discapacidad';
      }
    }

    // Para Comei se necesita autorización si hay id de autorización por incremento de cupo
    if (formData.insurer_id === 22 && formData.authorization_id) {
      newRequired.unshift('autorizacion');
    }

    // Para OSSEG se necesita autorización si hay id de autorización por incremento de cupo
    if (formData.insurer_id === 35) {
      if (formData.additional_id === 13) {
        newRequired.push('autorizacion_domicilio');
      }

      if (formData.extended_quota) {
        newRequired.push('autorizacion');
      }

      if (formData.additional_id === 13 || formData.extended_quota) {
        newRequired.push('planilla_asistencia');
      }
    }

    if (formData.insurer_id === 36) {
      if (
        !formData.previously_authorized &&
        formData.practice_id !== 14 && formData.additional_id !== 14
      ) {
        newRequired = newRequired.filter(r => r !== 'planilla_asistencia');
      }

      // Para Avalian se necesita el Certificado Federado si es Especialidad Deportología
      if (formData.practice_id == 40) {
        attachsLabels['constancia_deportiva'] = 'Certificado Federado';
        newRequired.push('constancia_deportiva');
      }
    }

    if ([49].includes(formData.insurer_id)) {
      attachsLabels['autorizacion'] = 'Autorización AMEBPBA';
    }

    // Para Galeno y GalenoDan las especialidades y orientaciones necesitan Autorización de Axis
    if ([48, 50].includes(formData.insurer_id)) {
      attachsLabels['autorizacion'] = 'Autorización Galeno';

      if (
        [15, 25, 38, 39, 40].includes(formData.practice_id ?? 0) ||
        [15, 25, 38, 39, 40].includes(formData.additional_id ?? 0)
      ) {
        newRequired.push('axis_autorizacion');
        attachsLabels['axis_autorizacion'] = 'Autorización de Axis';
      }
    }

    // Para Federada Cuota Extendida se necesita planilla de asistencia y autorización de la OS
    if (formData.insurer_id === 74 && !!formData.extended_quota) {
      newRequired.push('planilla_asistencia');
      newRequired.push('autorizacion');
    }

    // Para Amsterdam se necesita planilla de asistencia porque el token no funciona
    if (formData.insurer_id === 80) {
      newRequired.unshift('planilla_asistencia');
    }

    // Para CASA: planilla de asistencia solo para Internación, autorización para RPG/Vestibular/Drenaje
    if (formData.insurer_id === 47) {
      // Planilla de asistencia solo para Módulo Internación (practice_id 14)
      if (
        [9, 10, 14, 17].includes(formData.practice_id ?? 0) ||
        formData.additional_id === 14
      ) {
        newRequired.push('planilla_asistencia');
      }

      // Autorización previa para RPG (9), Vestibular (17) y Drenaje Linfático (10)
      if (formData.practice_id && [9, 10, 17].includes(formData.practice_id)) {
        newRequired.unshift('autorizacion');
        attachsLabels['autorizacion'] = 'Autorización Previa';
      }
    }

    // Para AMFFA se necesita autorización para la práctica RPG
    if (formData.insurer_id === 26 && formData.practice_id == 9) {
      newRequired.unshift('autorizacion');
    }

    // Para Omint se necesita autorización y planilla de asistencia para prácticas integradas
    if (
      formData.insurer_id === 63 &&
      (
        (!!formData.practice_id && [9, 11, 12, 17].includes(formData.practice_id)) ||
        (!!formData.additional_id && [11, 12].includes(formData.additional_id))
      )
    ) {
      newRequired.push('autorizacion', 'planilla_asistencia');
    }

    if (formData.exceptional) {
      newRequired.push('exceptional');
    }

    if (formData.rebilling) {
      newRequired.push('nota_debito');
    }

    if (formData.insurer_id == 57 && formData.practice_id == 40) {
      newRequired.push('constancia_deportiva');
    }

    setRequired(newRequired);
  }, [
    formData.insurer_id,
    formData.sessions.length,
    previouslyAuthorized,
    impairment,
    formData.rebilling,
    formData.exceptional,
    formData.practice_id,
    formData.additional_id,
  ]);

  useEffect(() => {
    switch (formData.insurer_id) {
      case 22: // COMEI
        setImpairment(formData.impairment);
        break;
      case 26: // AMFFA
        setImpairment(formData.impairment);
        break;
      case 74: // Federada
        setImpairment(
          formData.practice_id == 14 || formData.additional_id == 14,
        );
        break;
      default:
        setImpairment(formData.impairment);
    }
  }, [
    formData.insurer_id,
    formData.previously_authorized,
    formData.practice_id,
    formData.additional_id,
    formData.impairment,
  ]);

  return (
    <>
      <FormGroup>
        <Label>
          Adjuntos
          {
            editable
              ? <>
                  <br />
                  <small className='mt-2 text-muted'>
                    Tamaño máximo por archivo: <strong>{ bytesToSize(config.maxFileSize) }</strong>
                  </small>
                </>
              : null
          }
        </Label>
        <Row>
          {required.map(key => (
            <Col sm={6} key={key} className="mt-2">
              <label>
                {attachsLabels[key]}{' '}
                {getRequiredValue(key) ? <Required /> : null}
              </label>
              <div className={files[key] ? 'd-block' : 'd-none'}>
                <Button
                  color="primary"
                  onClick={() => handleFilePreview(key)}
                  disabled={isLoading}
                >
                  Ver adjunto
                </Button>
                {editable ? (
                  <>
                    <Button
                      color="warning"
                      onClick={() => handleFileReplace(key)}
                      disabled={isLoading}
                    >
                      Reemplazar
                    </Button>
                    <Button
                      onClick={() => handleFileRemove(key)}
                      disabled={isLoading}
                    >
                      Eliminar
                    </Button>
                  </>
                ) : null}
              </div>
              {editable ? (
                <div className={`custom-file ${files[key] ? 'd-none' : ''}`}>
                  <Input
                    id={`file_${key}`}
                    name={`file_${key}`}
                    data-key={key}
                    type="file"
                    accept={accept.join(', ')}
                    onChange={event =>
                      handleFileChange(key, event.target.files)
                    }
                    className="custom-file-input overflow-hidden"
                    disabled={isLoading}
                    required={getRequiredValue(key)}
                  />
                  <label className="custom-file-label" htmlFor={`file_${key}`}>
                    Elegir archivo...
                  </label>
                  <label
                    className={`required-text mt-2 ${
                      sizeError !== key ? 'hidden' : ''
                    }`}
                    style={{ fontSize: '80%' }}
                  >
                    El tamaño del archivo no debe superar los 3 MB
                  </label>
                  <label
                    className={`required-text mt-2 ${
                      emptyError !== key ? 'hidden' : ''
                    }`}
                    style={{ fontSize: '80%' }}
                  >
                    El archivo que está intentando subir está vacio. Verifique
                    que no sea un enlace directo y que sea válido
                  </label>
                  <label
                    className={`required-text mt-2 ${
                      typeError !== key ? 'hidden' : ''
                    }`}
                    style={{ fontSize: '80%' }}
                  >
                    El formato del archivo no está soportado. Únicamente se
                    pueden adjuntar imágenes JPG y PNG o documentos PDF
                  </label>
                </div>
              ) : null}
            </Col>
          ))}
        </Row>
      </FormGroup>

      <AttachModal
        data={attachModal}
        onClose={() => setAttachModal({ ...attachModal, show: false })}
      />
    </>
  );
}
